import axios from 'axios';
import { prefetchedProgram, shareInFlight } from '@/utils/programPrefetch';
import * as Sentry from '@sentry/vue';
import { ActionTree, ActionContext } from 'vuex';
import {
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
  clearAuth,
  getAccessToken,
  setItem as secureSetItem
} from '@/services/secureStorage';
import {
  ProductInfoArraySchema,
  PathwaysArraySchema,
  TeamListSchema,
  StudentDocumentSchema,
  safeValidate
} from '@/services/schemas/api-responses';
import nexusClient from '@/apollo/nexusClient';
import { AWARD_ACTIVITY_MERITS_MUTATION } from '@/services/graphql/nexus/merits';
import {
  USER_PORTFOLIO_QUERY,
  UPDATE_PORTFOLIO_MUTATION
} from '@/services/graphql/nexus/portfolio';
import {
  PortfolioUpdateType,
  GetBestStudentInstanceDocument,
  StudentByIdRawDocument,
  ProgramByIdRawDocument
} from '@/generated/nexus-graphql';
import gql from 'graphql-tag';
import { attemptTokenRefresh } from '@/utils/tokenRefreshTimer';
import { ME_QUERY } from '@/services/graphql/nexus/auth';
import type { MeQuery } from '@/generated/nexus-graphql/auth';
import { UPLOAD_PROFILE_IMAGE_MUTATION, REMOVE_PROFILE_IMAGE_MUTATION } from '@/graphql';
import {
  SPONSOR_LINK_BY_SHARE_CODE_QUERY,
  ORGANIZATION_BASIC_INFO_QUERY
} from '@/services/graphql/nexus/sponsors';
import {
  CREATE_ACTIVITY_SCHEMA_MUTATION,
  UPDATE_ACTIVITY_SCHEMA_MUTATION,
  DELETE_ACTIVITY_SCHEMA_MUTATION,
  GET_ACTIVITY_SCHEMA_LIBRARY_QUERY,
  CLONE_ACTIVITY_SCHEMA_TO_PROGRAM_MUTATION,
  type CreateActivitySchemaInput,
  type UpdateActivitySchemaInput,
  type ActivitySchemaLibraryFilter,
  type ActionResult,
  type ActivitySchema,
  type ActivitySchemaLibraryResult,
  type CloneActivitySchemaResult
} from '@/services/graphql/nexus/activitySchema';
import {
  REATTACH_VIDEOASK_RESPONSE_MUTATION,
  DELETE_VIDEOASK_RESPONSE_MUTATION,
  REFRESH_VIDEOASK_TRANSCRIPTION_MUTATION,
  type ReattachVideoAskInput,
  type DeleteVideoAskInput,
  type VideoAskOperationResult
} from '@/services/graphql/nexus/videoask';
import {
  CREATE_ENDORSEMENT_REQUEST,
  RESEND_ENDORSEMENT_EMAIL,
  type CreateEndorsementRequestInput,
  type ResendEndorsementEmailInput,
  type CreateEndorsementRequestResponse,
  type ResendEndorsementEmailResponse
} from '@/services/graphql/nexus/endorsement';
import { RootState } from '../../state';
import { MutationTypes } from './mutations';
import userState from './state';
import activityList from './const';

/* --- portfolio writes (flag VITE_PORTFOLIO_WRITE_NEXUS) ---------------------
 * OFF (default) = the legacy user-service PATCH, byte-for-byte today's behaviour.
 * ON            = the Nexus updatePortfolio mutation.
 *
 * This is flagged separately from the READ cutover on purpose. These eight save
 * screens have been returning 400 since a commit dropped `_id` from their
 * payload, so they have not written anything for months. Turning them back on is
 * not a fix by itself: updateParentPortfolio, updateSchoolPortfolio and
 * updateOrganizerPortfolio are replace-style, so a form that rendered blank and
 * was then saved would WIPE the row. Hence the empty-payload guard below.
 */
const PORTFOLIO_WRITE_NEXUS = import.meta.env.VITE_PORTFOLIO_WRITE_NEXUS === 'true';

/** Vuex callers name the collection; the mutation takes an enum. */
const PORTFOLIO_UPDATE_TYPES: Record<string, PortfolioUpdateType> = {
  User: PortfolioUpdateType.User,
  StudentPortfolio: PortfolioUpdateType.Student,
  TeacherPortfolio: PortfolioUpdateType.Teacher,
  EmployerPortfolio: PortfolioUpdateType.Employer,
  ParentPortfolio: PortfolioUpdateType.Parent,
  SchoolPortfolio: PortfolioUpdateType.School,
  OrganizerPortfolio: PortfolioUpdateType.Organizer
};

/**
 * The eight save screens branch on `if (!res?.response?.data?.error)` - an axios
 * error shape that only ever existed because the old catch returned the error
 * object. Keep producing it so a Nexus failure still READS as a failure there,
 * while staying truthy for SettingsName's `if (res)` navigation gate.
 */
function portfolioWriteError(description: string) {
  return { response: { data: { error: { code: 'GraphQLError', description } } } };
}

/** True when a payload carries no actual content - every leaf blank. */
function isEmptyPayload(payload: any): boolean {
  let sawLeaf = false;
  const walk = (v: any): boolean => {
    if (v === null || v === undefined || v === '') return true;
    if (Array.isArray(v)) return v.every(walk);
    if (typeof v === 'object') return Object.values(v).every(walk);
    sawLeaf = true;
    return false;
  };
  const allBlank = walk(payload);
  return allBlank && !sawLeaf;
}

// eslint-disable-next-line no-shadow
export enum ActionTypes {
  loginUser = 'loginUser',
  fetchUser = 'fetchUser',
  refreshToken = 'refreshToken',
  forcedLogout = 'forcedLogout',
  fetchStudentById = 'fetchStudentById',
  fetchProgramById = 'fetchProgramId',
  fetchTeamDoc = 'fetchTeamDoc',
  fetchTeamList = 'fetchTeamList',
  updateProgramState = 'updateProgramState',
  updateProgramData = 'updateProgramData',
  getProgram = 'getProgram',
  makeOffer = 'makeOffer',
  createStudent = 'createStudent',
  getDataWithShareableUrl = 'getDataWithShareableUrl',
  addNewAdk = 'adkNewAdk',
  getUserPermissions = 'getUserPermissions',
  deleteAdkOrCategory = 'deleteAdkOrCategory',
  updateCompleted = 'updateCompleted',
  editCategory = 'editCategory',
  getSponsorLinkByShareCode = 'getSponsorLinkByShareCode',
  fetchUserById = 'fetchUserById',
  getOrganizationByLink = 'getOrganizationByLink',
  getOrganizationById = 'getOrganizationById',
  getUserByKeyAndValue = 'getUserByKeyAndValue',
  // ActivitySchema CRUD actions
  createActivitySchema = 'createActivitySchema',
  updateActivitySchema = 'updateActivitySchema',
  deleteActivitySchema = 'deleteActivitySchema',
  getActivitySchemaLibrary = 'getActivitySchemaLibrary',
  cloneActivitySchemaToProgram = 'cloneActivitySchemaToProgram'
}
type UserActionCtx = ActionContext<typeof userState, RootState>;

export interface UserActions extends ActionTree<typeof userState, RootState> {
  loginUser: (ctx: UserActionCtx, payload) => Promise<void>;
  updateProgramState: (ctx: UserActionCtx, data: any) => void;
  clearProgramDoc: (ctx: UserActionCtx) => void;

  /**
   * Index signature added to allow any additional dynamically named Vuex actions
   * to be typed, preventing TypeScript compilation errors when consuming
   * useUserActions with action names that are not explicitly declared above.
   */
  [key: string]: any;
}

export const actions: UserActions = {
  updateProgramState({ commit }, data) {
    commit(MutationTypes.GET_PROGRAM, data);
  },

  clearProgramDoc({ commit }) {
    commit(MutationTypes.CLEAR_PROGRAM);
  },

  async loginUser({ commit, state }, data) {
    try {
      // Ensure the user object always carries a Mongo-style _id so downstream code
      // (e.g. getLatestDocumentByUserId) never sees an empty string.
      const normalizedUser = {
        ...data,
        _id:
          data?._id ||
          data?.id ||
          data?.userId ||
          (data?.user ? data.user._id || data.user.id || data.user.userId : undefined) ||
          ''
      };

      // Preserve existing profile if the incoming user doesn't have one
      // This prevents auth refresh from wiping out profile uploaded via Nexus
      const existingProfile = state?.user?.profile;
      if (!normalizedUser.profile && existingProfile) {
        normalizedUser.profile = existingProfile;
      }

      // Persist via secureStorage helper so both cookie+LS strategies stay consistent
      if (typeof window !== 'undefined') {
        secureSetItem('user', JSON.stringify(normalizedUser));
      }

      commit(MutationTypes.LOGIN_USER, normalizedUser);

      // Tie Sentry events to this account so every error maps to a real user — this is
      // what lets the autofix DB-grounding look up the affected record by email/id.
      try {
        const Sentry = await import('@sentry/vue');
        Sentry.setUser({
          id: normalizedUser._id || normalizedUser.userId || undefined,
          email: normalizedUser.email || undefined,
          username: normalizedUser.fullName || undefined,
          userTypes: normalizedUser.userTypes
        });
      } catch {
        /* never block login on telemetry */
      }

      // Query portfolio completeness once per login
      try {
        const { IS_PORTFOLIO_COMPLETE_QUERY } = await import('@/graphql');
        const { default: nexusClient } = await import('@/apollo/nexusClient');
        const { data: pcData } = await nexusClient.query<{ authIsPortfolioComplete: boolean }>({
          query: IS_PORTFOLIO_COMPLETE_QUERY,
          fetchPolicy: 'network-only'
        });
        const flag = pcData?.authIsPortfolioComplete === true;

        // Persist flag locally for hard reloads
        if (typeof window !== 'undefined') {
          secureSetItem('profileComplete', JSON.stringify(flag));
        }
        commit(MutationTypes.SET_PROFILE_COMPLETE, flag);
      } catch (pcErr) {
        console.warn('[Vuex] Portfolio completeness query failed:', pcErr);
        // Fallback: if user exists and has data, assume profile is complete
        const hasUserData =
          normalizedUser?._id &&
          (normalizedUser.firstName || normalizedUser.lastName || normalizedUser.email);
        commit(MutationTypes.SET_PROFILE_COMPLETE, hasUserData);

        // Persist fallback flag locally
        if (typeof window !== 'undefined') {
          secureSetItem('profileComplete', JSON.stringify(hasUserData));
        }
      }

      return data;
    } catch (err) {
      commit(MutationTypes.LOGIN_ERROR, err);
      throw err;
    }
  },

  async refreshToken({ commit, dispatch }, data) {
    try {
      const tknData = {
        refresh_token: getRefreshToken()
      };
      const resp = await axios.post(
        `${import.meta.env.VITE_AUTH0}/generate-access-token`,
        tknData,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      if (resp.data.data) {
        setAccessToken(resp.data.data.access_token);
        setRefreshToken(resp.data.data.refresh_token);
        window.location.href = `/portfolio`;
      }
    } catch (error) {
      console.log(error);
    }
  },
  // eslint-disable-next-line consistent-return
  async getAuthUrl({ commit, dispatch }, data) {
    try {
      let google;
      if (data?.type === 'google') {
        google = true;
      } else if (data?.type === 'outlook') {
        google = false;
      }
      const response = await axios.get(
        `${import.meta.env.VITE_CALENDAR_SERVICE}/auth-url?redirect_uri=${
          window.location.origin
        }/meetings&google=${google}&public_id=${data?.public_id}`
      );
      return response.data.data;
    } catch (error) {
      console.log(error);
    }
  },
  // eslint-disable-next-line consistent-return
  async refreshCalendarTokenData({ commit, dispatch }, payload) {
    try {
      let google;
      if (payload.type === 'google') {
        google = true;
      } else if (payload.type === 'outlook') {
        google = false;
      }
      const response = await axios.get(
        `${import.meta.env.VITE_CALENDAR_SERVICE}/refresh-access-token?refresh_token=${
          payload.refreshToken
        }&google=${google}&public_id=${payload?.document_id}&document_id=${
          payload?.document_id
        }&document_type=${payload?.document_type}&adk_type=${payload?.adk_type}`
      );
      return response.data;
    } catch (error) {
      console.log(error);
    }
  },
  // eslint-disable-next-line consistent-return
  async createCalendar({ commit, dispatch }, data) {
    try {
      const response: any = await axios.post(
        `${import.meta.env.VITE_CALENDAR_SERVICE}/calendar?token=${data.accessToken}&google=${
          data.google
        }&adkName=${data.adkName}`,
        data.eventData
      );
      return response.data;
    } catch (error) {
      if (error.response.data.error.code === 'UNAUTHORIZED') {
        const refreshTokenData: any = await axios.get(
          `${import.meta.env.VITE_CALENDAR_SERVICE}/refresh-access-token?refresh_token=${
            data.refreshToken
          }&google=${data.google}`
        );
        const responseData: any = await await axios.post(
          `${import.meta.env.VITE_CALENDAR_SERVICE}/calendar?token=${
            refreshTokenData.data.data.access_token
          }&google=${data.google}`,
          data.eventData
        );
        return responseData.data;
      }
      console.log(error);
    }
  },
  // eslint-disable-next-line consistent-return
  async updateCalendar({ commit, dispatch }, data) {
    try {
      const response: any = await axios.patch(
        `${import.meta.env.VITE_CALENDAR_SERVICE}/calendar?token=${data.accessToken}&event_id=${
          data.eventId
        }&google=${data.google}&adkName=${data.adkName}`,
        data.eventData,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      if (error.response.data.error.code === 'UNAUTHORIZED') {
        const refreshTokenData: any = await axios.get(
          `${import.meta.env.VITE_CALENDAR_SERVICE}/refresh-access-token?refresh_token=${
            data.refreshToken
          }&google=${data.google}`
        );
        const responseData: any = await axios.patch(
          `${import.meta.env.VITE_CALENDAR_SERVICE}/calendar?token=${
            refreshTokenData.data.data.access_token
          }&event_id=${data.eventId}&google=${data.google}`,
          data.eventData,
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
        return responseData.data;
      }
      console.log(error, 'error');
    }
  },
  // eslint-disable-next-line consistent-return
  async getCalendarTokenData({ commit, dispatch }, tokenData) {
    try {
      let google;
      if (tokenData.type === 'google') {
        google = true;
      } else if (tokenData.type === 'outlook') {
        google = false;
      }
      const response = await axios.get(
        `${import.meta.env.VITE_CALENDAR_SERVICE}/auth-token?code=${tokenData.code}&redirect_uri=${
          window.location.origin
        }/meetings&google=${google}&public_id=${tokenData?.public_id}&document_id=${
          tokenData?.document_id
        }&document_type=${tokenData?.document_type}&adk_index=${tokenData?.adk_index}&adk_type=${
          tokenData?.adk_type
        }`
      );
      return response.data;
    } catch (error) {
      console.log(error);
    }
  },
  // eslint-disable-next-line consistent-return
  async getCalendarEvents({ commit, dispatch }, data) {
    try {
      let query;
      if (data.calendarData.calendarType === 'month') {
        query = `token=${data.accessToken}&year=${data.calendarData.year}&month=${data.calendarData.month}&google=${data.google}`;
      } else if (data.calendarData.calendarType === 'day') {
        query = `token=${data.accessToken}&year=${data.calendarData.year}&month=${data.calendarData.month}&day=${data.calendarData.day}&google=${data.google}`;
      } else if (
        data.calendarData.calendarType === '4day' ||
        data.calendarData.calendarType === 'week'
      ) {
        const weekDays = {
          start_time: data.calendarData.startDate,
          end_time: data.calendarData.endDate
        };
        query = `token=${data.accessToken}&week=${encodeURIComponent(
          JSON.stringify(weekDays)
        )}&google=${data.google}`;
      } else {
        const weekDays = {
          start_time: data.calendarData.startDate,
          end_time: data.calendarData.endDate
        };
        query = `token=${data.accessToken}&week=${encodeURIComponent(
          JSON.stringify(weekDays)
        )}&google=${data.google}`;
      }
      console.log('data', data);
      if (data?.public_id) {
        query = `${query}&public_id=${data?.public_id}`;
      }
      const response = await axios.get(
        `${import.meta.env.VITE_CALENDAR_SERVICE}/calendar?${query}`
      );
      return response.data;
    } catch (error) {
      console.log(error);
    }
  },

  // eslint-disable-next-line consistent-return
  async getInternshipBadge({ commit, dispatch }, data) {
    try {
      let queryParams = `program_id=${data?.program_id}`;
      if (data?.public_id) {
        queryParams = `${queryParams}&public_id=${data?.public_id}`;
      }
      const response = await axios.get(
        `${import.meta.env.VITE_CALENDAR_SERVICE}/internship-badge?${queryParams}`
      );
      return response.data.data;
    } catch (error) {
      console.log(error);
    }
  },

  // eslint-disable-next-line consistent-return
  async deleteCalendarEvent({ commit, dispatch }, data) {
    try {
      const response = await axios.delete(
        `${import.meta.env.VITE_CALENDAR_SERVICE}/calendar?token=${data.accessToken}&event_id=${
          data.eventId
        }&google=${data.google}`
      );
      return response.data;
    } catch (error) {
      console.log(error);
    }
  },
  async fetchUser({ commit, dispatch, state }, data) {
    try {
      const { data: result } = await nexusClient.query<MeQuery>({
        query: ME_QUERY,
        fetchPolicy: 'network-only'
      });

      if (!result?.me) throw new Error('No user data returned from me query');

      const { me } = result;
      const userData = {
        _id: me.dbUser?.id || me.userId || '',
        userId: me.userId,
        email: me.email,
        emailVerified: me.emailVerified || false,
        phoneNumber: (me as any).phoneNumber || null,
        phoneVerified: (me as any).phoneVerified || false,
        firstName: me.firstName,
        lastName: me.lastName,
        fullName: me.fullName,
        profileImg: me.profileImg,
        profile: (me as any).profile,
        shareCode: me.shareCode,
        userTypes: me.userTypes || [],
        stripeConnectId: me.stripeConnectId || null,
        stripeVerified: me.stripeVerified || null,
        slug: (me.dbUser as any)?.slug || null,
        portfolioPublic: (me.dbUser as any)?.portfolioPublic ?? null
      };

      commit(MutationTypes.LOGIN_USER, userData);
      return userData;
    } catch (err: any) {
      console.warn('[fetchUser] Failed:', err?.message || err);
      commit(MutationTypes.LOGIN_ERROR, err);
      throw err;
    }
  },
  async refreshDiscordAccessToken({ commit, dispatch }, data) {
    try {
      const resp = await axios.get(
        `${import.meta.env.VITE_COWORK_SERVICE}/refresh-token?user_type=${
          data.userType
        }&program_id=${data.programId}&public_id=${data.public_id}&refresh_token=${
          data.refreshToken
        }`
      );
      return resp.data;
    } catch (err) {
      console.log(err.response);
      return err.response.data;
    }
  },
  // eslint-disable-next-line consistent-return
  async createDiscordChannels({ commit, dispatch }, data) {
    try {
      const resp = await axios.post(`${import.meta.env.VITE_COWORK_SERVICE}/channels`, data);
      return resp;
    } catch (err) {
      console.log(err.response);
    }
  },
  async sendMessageInDiscord({ commit, dispatch }, data) {
    try {
      const resp = await axios.post(`${import.meta.env.VITE_COWORK_SERVICE}/send-message`, data);
      return resp;
    } catch (err) {
      console.log(err.response);
      return err.response.data;
    }
  },
  // eslint-disable-next-line consistent-return
  async addMembersToDiscordRole({ commit, dispatch }, data) {
    try {
      const resp = await axios.post(`${import.meta.env.VITE_COWORK_SERVICE}/add-to-role`, data);
      return resp;
    } catch (error) {
      console.log(error);
    }
  },
  // eslint-disable-next-line consistent-return
  async fetchProgramById({ commit, dispatch }, data) {
    try {
      const { data: result } = await nexusClient.query({
        query: ProgramByIdRawDocument,
        variables: { id: data.program_id },
        fetchPolicy: 'network-only'
      });
      const programData = result?.programByIdRaw;
      // Deep clone to detach from Apollo's frozen cache — downstream code mutates this data
      const cloned = programData ? JSON.parse(JSON.stringify(programData)) : programData;
      commit(MutationTypes.GET_PROGRAM, cloned);
      return cloned;
    } catch (err) {
      console.log(err);
    }
  },
  // eslint-disable-next-line consistent-return
  async getDocumentByIdAndType({ commit, dispatch }, data) {
    try {
      let queryParams = `document_type=${data.document_type}&document_id=${data.document_id}`;
      if (data?.public_id) {
        queryParams = `${queryParams}&public_id=${data?.public_id}`;
      }
      const resp = await axios.get(`${import.meta.env.VITE_PROGRAM_DOC}/document?${queryParams}`);
      return resp.data.data;
    } catch (err) {
      console.log(err);
    }
  },
  // eslint-disable-next-line consistent-return
  async fetchUserType({ commit, dispatch }, data) {
    try {
      const { data: result } = await nexusClient.query<{ getUserRoleForProgram: string | null }>({
        query: gql`
          query GetUserRoleForProgram($programId: String!) {
            getUserRoleForProgram(programId: $programId)
          }
        `,
        variables: { programId: data.program_id },
        fetchPolicy: 'network-only'
      });
      commit(MutationTypes.SET_ROLE, result?.getUserRoleForProgram);
    } catch (err) {
      console.log(err);
    }
  },

  async fetchTeamDoc({ commit, dispatch }, data) {
    // A logged-out visitor on a program link has no user id. The query declares
    // `$userId: String!`, so sending `undefined` is a 400 on the wire and an error line
    // in the server log on every such visit (seen 2026-09-05). No user, no team - the
    // same answer the "no team found" branch below already gives.
    if (!data?.user_id) {
      return { error: false, status: 400, message: 'No team found.' };
    }
    try {
      // STUDENT_INSTANCES: only when a caller supplies `student_id` (which happens
      // solely under isFeatureEnabled('STUDENT_INSTANCES')) do we use the extended
      // operation that carries `studentId`, letting the backend scope the team to the
      // loaded instance via adks[team].team_id. When absent, the ORIGINAL operation is
      // sent verbatim — the on-the-wire query is byte-identical to today, so flag-off
      // (and any deploy that predates the instance-aware resolver) is unaffected.
      const hasStudentId = !!data.student_id;
      const { data: result } = await nexusClient.query({
        query: hasStudentId
          ? gql`
              query TeamDocByUser($userId: String!, $programId: String!, $studentId: String) {
                teamDocByUser(userId: $userId, programId: $programId, studentId: $studentId)
              }
            `
          : gql`
              query TeamDocByUser($userId: String!, $programId: String!) {
                teamDocByUser(userId: $userId, programId: $programId)
              }
            `,
        variables: hasStudentId
          ? { userId: data.user_id, programId: data.program_id, studentId: data.student_id }
          : { userId: data.user_id, programId: data.program_id },
        fetchPolicy: 'network-only'
      });
      const team = result?.teamDocByUser;
      if (!team) {
        // No team found — equivalent to old 400 status ("no team exists")
        return { error: false, status: 400, message: 'No team found.' };
      }
      // Deep clone to detach from Apollo's frozen cache
      return JSON.parse(JSON.stringify(team));
    } catch (err: any) {
      console.error('[fetchTeamDoc] Error:', {
        user_id: data.user_id,
        program_id: data.program_id,
        error: err.message
      });
      return {
        error: true,
        status: err.networkError?.statusCode || 500,
        message: err.message || 'Request failed'
      };
    }
  },

  async fetchTeamList({ commit, dispatch }, data) {
    try {
      const { data: result } = await nexusClient.query({
        query: gql`
          query TeamsByProgram($programId: String!) {
            teamsByProgram(programId: $programId)
          }
        `,
        variables: { programId: data.program_id },
        fetchPolicy: 'network-only'
      });
      return safeValidate(TeamListSchema, result?.teamsByProgram || [], [], 'fetchTeamList');
    } catch (err) {
      console.log(err);
      return [];
    }
  },

  // eslint-disable-next-line consistent-return
  async fetchStudentById({ commit, dispatch }, data) {
    try {
      // If student_id is provided, fetch by specific ID (for instance switching / monitor mode)
      if (data?.student_id) {
        const { data: result } = await nexusClient.query({
          query: StudentByIdRawDocument,
          variables: { id: data.student_id },
          fetchPolicy: 'network-only'
        });
        const studentDoc = result?.studentByIdRaw;
        if (!studentDoc) throw new Error('Student not found');

        // Deep clone to detach from Apollo's frozen cache
        const cloned = JSON.parse(JSON.stringify(studentDoc));
        commit(MutationTypes.GET_PROGRAM, cloned);
        return cloned;
      }

      // Guard against missing user_id
      if (!data?.user_id) {
        console.error('[fetchStudentById] Missing user_id:', { data });
        return {
          error: true,
          status: 400,
          message: 'User ID is required'
        };
      }

      // Use Nexus GraphQL to get best instance using smart selection
      const { data: queryResult } = await nexusClient.query({
        query: GetBestStudentInstanceDocument,
        variables: {
          input: {
            participantId: data.user_id,
            programId: data.program_id
          }
        },
        fetchPolicy: 'network-only' // Always fetch fresh data
      });

      const bestInstance = queryResult?.getBestStudentInstance;

      if (!bestInstance || !bestInstance.id) {
        // No instances found - return error to trigger student creation
        return {
          data: {
            error: true
          }
        };
      }

      // Fetch the full selected instance via Nexus GraphQL (raw JSON)
      const { data: fullResult } = await nexusClient.query({
        query: StudentByIdRawDocument,
        variables: { id: bestInstance.id },
        fetchPolicy: 'network-only'
      });
      const studentDoc = fullResult?.studentByIdRaw;

      if (!studentDoc) {
        throw new Error('Invalid response from server - missing data');
      }

      // Deep clone to detach from Apollo's frozen cache, then merge checkout fields
      const studentData = {
        ...JSON.parse(JSON.stringify(studentDoc)),
        isProgramCheckout: bestInstance.isProgramCheckout ?? studentDoc.isProgramCheckout,
        isProgramCheckoutBy: bestInstance.isProgramCheckoutBy ?? studentDoc.isProgramCheckoutBy
      };

      commit(MutationTypes.GET_PROGRAM, studentData);
      return studentData;
    } catch (err: any) {
      console.error('[fetchStudentById] Error:', {
        user_id: data?.user_id,
        program_id: data?.program_id,
        student_id: data?.student_id,
        error: err.message
      });
      return {
        error: true,
        status: err.response?.status || 0,
        message: err.message || 'Failed to fetch student data'
      };
    }
  },

  // eslint-disable-next-line consistent-return
  async updateAdkData({ commit, dispatch }, data) {
    try {
      const resp = await axios.patch(
        `${import.meta.env.VITE_PROGRAM_DOC}/program-adk?document_type=${
          data.document_type
        }&document_id=${data.program_id}`,
        data.data
      );
      // Only commit to programDoc store for Program/Student/ProgramSchema documents.
      // ProgramTeam documents should NOT overwrite programDoc — they are a separate
      // data source (teamDocument in Renderer). Committing team data here corrupts
      // the store and causes stale isSaved/validation checks.
      if (data.document_type !== 'ProgramTeam') {
        commit(MutationTypes.GET_PROGRAM, resp.data.data);
      }
      return resp.data;
    } catch (err) {
      // Surface the failure (the program-management-service save can time out silently);
      // the caller (Renderer.updateProgramAdk) handles undefined/error responses without
      // wiping the participant's in-memory answers.
      Sentry.captureException(err, {
        tags: { action: 'updateAdkData' },
        extra: { documentType: data?.document_type, programId: data?.program_id }
      });
      return err.response;
    }
  },
  async addNewAdk({ commit }, data) {
    try {
      const resp = await axios.post(`${import.meta.env.VITE_PROGRAM_DOC}/add-program-adk`, {
        programOrTemplateId: data.programOrTemplateId,
        adk_name: data.adk_name,
        data: data.data,
        document_type: data.document_type,
        isCatMatch: data.isCatMatch,
        index: data.index
      });
      return resp.data;
    } catch (err) {
      return err.response;
    }
  },
  async deleteAdkOrCategory({ commit }, payload) {
    try {
      const resp = await axios.post(`${import.meta.env.VITE_PROGRAM_DOC}/delete-adk`, payload);
      return resp.data;
    } catch (err) {
      return err.response;
    }
  },
  async addCategory({ commit }, payload) {
    try {
      const resp = await axios.post(`${import.meta.env.VITE_PROGRAM_DOC}/add-category`, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      commit(MutationTypes.GET_PROGRAM, resp.data.data);
      return resp.data;
    } catch (err) {
      return err.response;
    }
  },
  async editCategory({ commit }, data) {
    try {
      const resp = await axios.post(
        `${import.meta.env.VITE_PROGRAM_DOC}/edit-category`,
        {
          program_id: data.programId,
          category_name: data.categoryName,
          document_type: data.documentType,
          old_category: data.oldCategory,
          icon: data.icon
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return resp.data;
    } catch (err) {
      return err.response;
    }
  },
  async updateProgramData({ commit, dispatch }, data) {
    try {
      // SAFETY (centralized adks guard): this hits the legacy program-doc PATCH,
      // which $set-merges whatever it receives. Program activities (`adks`) are
      // persisted via the Nexus mutations (createAutomationAdk/updateAutomationAdk),
      // NOT here. Many callers send the whole in-memory program doc, whose `adks`
      // can be stale/empty — a $set with that NULLS the real activities (the
      // recurring publish/toggle/anonymous-join wipe). So strip `adks` from the
      // payload unless a caller explicitly opts in (Builder, the activity editor,
      // passes `preserveAdks: true`).
      let payload = data?.data;
      if (payload && typeof payload === 'object' && 'adks' in payload && !data.preserveAdks) {
        const { adks: _omitAdks, ...rest } = payload as Record<string, any>;
        payload = rest;
      }
      const resp = await axios.patch(
        `${import.meta.env.VITE_PROGRAM_DOC}/program?program_id=${data.program_id}`,
        payload
      );
      return resp.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },
  async publishProgram({ commit, dispatch }, data) {
    try {
      const resp = await axios.post(`${import.meta.env.VITE_PROGRAM_DOC}/program/publish`, data);
      commit(MutationTypes.GET_PROGRAM, resp.data.data);
      return resp.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },
  async updateAdksPosition({ commit, dispatch }, data) {
    try {
      const resp = await axios.patch(
        `${import.meta.env.VITE_PROGRAM_DOC}/program/manage-adks?document_id=${
          data.document_id
        }&document_type=${data.document_type}`,
        { adks: data.adks, categories: data.categories }
      );
      commit(MutationTypes.GET_PROGRAM, resp.data.data);
      return resp.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },
  async deleteVideoAskResponse({ commit, dispatch }, data) {
    try {
      const resp = await axios.delete(
        `${import.meta.env.VITE_VIDEOASK_SERVICE}/videoask-response?user_type=${
          data.user_type
        }&program_id=${data.program_id}&public_id=${data.public_id}&adk_name=${
          data.adk_name
        }&key_name=${data.key_name}`
      );
      commit(MutationTypes.GET_PROGRAM, resp.data.data);
      return resp.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },
  getProgram({ commit, dispatch }, data) {
    const document_type = 'Program';
    return new Promise((resolve, reject) => {
      axios
        .get(
          `${
            import.meta.env.VITE_PROGRAM_DOC
          }/document?document_type=${document_type}&document_id=${data.program_id}`
        )
        .then(response => {
          resolve(response.data.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  },
  async getAdkIndexByName({ commit, dispatch }, adkName) {
    let index = 0;
    if (activityList.includes(adkName.toLowerCase())) {
      index = activityList.indexOf(adkName.toLowerCase());
    }
    return index;
  },
  // eslint-disable-next-line consistent-return
  async getPrivacyPolicy() {
    try {
      const response = await axios.get(`https://www.iubenda.com/api/privacy-policy/21126817`);
      return response.data;
    } catch (error) {
      console.log(error);
    }
  },

  // eslint-disable-next-line consistent-return
  async getTermsAndConditions() {
    try {
      const response = await axios.get(`https://www.iubenda.com/api/terms-and-conditions/21126817`);
      return response.data;
    } catch (error) {
      console.log(error);
    }
  },

  async forcedLogout() {
    clearAuth();
    window.location.href = `/login?forcedLogout=true`;
  },

  async revertVersion({ commit, dispatch }, data) {
    try {
      const resp = await axios.post(
        `${import.meta.env.VITE_REVISION_SERVICE}/revision/revert`,
        data
      );
      commit(MutationTypes.GET_PROGRAM, resp.data.data);
      return resp.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },
  /**
   * One person's portfolio, from Nexus.
   *
   * `data.user_id` is accepted and IGNORED. Every caller is asking for themselves, and
   * `userPortfolio` derives the owner from the token. The by-id form would 403 here: the
   * store's `_id` is `me.dbUser?.id || me.userId`, so whenever dbUser is null it holds an
   * Auth0 sub rather than a Mongo id. Keeping the argument means no call site changes.
   *
   * Three contracts this must not break, all load-bearing:
   *  - Resolve, never reject. All eight save screens gate on `!res?.response?.data?.error`,
   *    which is an axios error shape that only ever appeared because the old catch returned
   *    the error. Throwing here takes every one of them down.
   *  - Return `{}` on failure, never null. Six ID screens read `d.Student` with no optional
   *    chaining, and m-checkout-form's own guard checks `!portfoliosData`.
   *  - Deep clone. m-checkout-form commits this into Vuex and UPDATE_STUDENT_PORTFOLIO then
   *    mutates that same subtree IN PLACE; Apollo's cached objects are frozen.
   */
  async getUserPortfolios({ commit, dispatch }, data) {
    try {
      const { data: result } = await nexusClient.query({
        query: USER_PORTFOLIO_QUERY,
        fetchPolicy: 'network-only',
        // One drifted document must not blank the other five sections. Several role fields
        // are non-null in the schema (OrganizerPortfolio.jobTitle/industry/work,
        // ParentPortfolio.email/parent, EmployerPortfolio.jobTitle/industry/primaryProduct),
        // so a null there bubbles up and, under Apollo's default errorPolicy 'none', throws
        // the ENTIRE result away. 'all' keeps the sections that did resolve.
        errorPolicy: 'all'
      });
      const p = (result as any)?.userPortfolio;
      if (!p) return {};
      // Deep clone to detach from Apollo's frozen cache - this object is mutated in place later.
      const cloned = JSON.parse(JSON.stringify(p));
      // The legacy endpoint answered with PascalCase role keys; every call site reads those.
      return {
        Student: cloned.student ?? null,
        Teacher: cloned.teacher ?? null,
        Employer: cloned.employer ?? null,
        Parent: cloned.parent ?? null,
        School: cloned.school ?? null,
        Organizer: cloned.organizer ?? null,
        User: cloned.user ?? null,
        completionStatus: cloned.completionStatus ?? null
      };
    } catch (err: any) {
      console.warn('[getUserPortfolios] failed:', err?.message || err);
      return {};
    }
  },
  async fetchUserById({ commit, dispatch }, data) {
    try {
      let queryparams = `document_type=User&document_id=${data?.user_id}`;
      if (data?.public_id) {
        queryparams = `${queryparams}&public_id=${data?.public_id}`;
      }
      const resp = await axios.get(`${import.meta.env.VITE_PROGRAM_DOC}/document?${queryparams}`);
      return resp.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },
  async publishProgramRevision({ commit, dispatch }, data) {
    try {
      const resp = await axios.post(`${import.meta.env.VITE_REVISION_SERVICE}/revision`, data);
      commit(MutationTypes.GET_PROGRAM, resp.data.data);
      return resp.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },
  async getProgramRevision({ commit, dispatch }, data) {
    try {
      const resp = await axios.get(
        `${import.meta.env.VITE_REVISION_SERVICE}/revision?program_id=${data.program_id}`
      );
      commit(MutationTypes.GET_PROGRAM, resp.data.data);
      return resp.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },
  // eslint-disable-next-line consistent-return
  async getUserByKeyAndValue({ commit, dispatch }, data) {
    try {
      const resp = await axios.get(
        `${import.meta.env.VITE_USER_SERVICE}/user/key?key=${data.key}&value=${data.value}`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return resp.data;
    } catch (err) {
      console.log(err);
    }
  },
  async updatePortfolio({ commit, dispatch }, data) {
    if (PORTFOLIO_WRITE_NEXUS) {
      try {
        const type = PORTFOLIO_UPDATE_TYPES[data.type];
        if (!type) {
          console.warn(`[updatePortfolio] unsupported type "${data.type}"`);
          return portfolioWriteError(`Unsupported portfolio type: ${data.type}`);
        }

        // `_id` was the legacy row selector; Nexus derives the owner from the
        // JWT. Dropping it makes m-checkout-form (which sends one) and the eight
        // screens (which do not) identical on the wire.
        const { _id, ...payload } = (data.payload ?? {}) as Record<string, unknown>;

        /* THE WIPE GUARD. Three of the update paths are replace-style, so an
           all-blank payload overwrites the row and drops its siblings - the
           geocode block included. A form only submits all-blank when its READ
           failed and it rendered empty, which is exactly the case worth
           refusing. A real edit always carries at least one value.
           Checked AFTER `_id` is stripped: `_id` is itself a non-blank leaf, so
           checking before it would make every checkout payload look non-empty. */
        if (isEmptyPayload(payload)) {
          console.warn('[updatePortfolio] refused an empty payload - the form never loaded');
          return portfolioWriteError('Nothing to save - reload and try again.');
        }

        const result = await nexusClient.mutate({
          mutation: UPDATE_PORTFOLIO_MUTATION,
          variables: { input: { type, data: payload } }
        });
        const response = (result as any).data?.updatePortfolio;
        if (!response?.success) {
          console.warn('[updatePortfolio] failed:', response?.message);
          return portfolioWriteError(response?.message || 'Portfolio update failed');
        }

        if (data.type === 'StudentPortfolio') {
          commit(MutationTypes.UPDATE_STUDENT_PORTFOLIO, data.payload);
        }
        // Truthy, and free of the axios error shape the save screens test for.
        return response.portfolio
          ? JSON.parse(JSON.stringify(response.portfolio))
          : { success: true };
      } catch (err: any) {
        // Never reject: the eight screens have no try/catch around the call.
        console.warn('[updatePortfolio] error:', err?.message || err);
        return portfolioWriteError(
          err?.graphQLErrors?.[0]?.message || err?.message || 'Portfolio update failed'
        );
      }
    }

    try {
      const resp = await axios.patch(
        `${import.meta.env.VITE_USER_SERVICE}/user-portfolio?type=${data.type}`,
        data.payload
      );
      // Update local store state so UI reflects the change immediately
      if (data.type === 'StudentPortfolio') {
        commit(MutationTypes.UPDATE_STUDENT_PORTFOLIO, data.payload);
      }
      return resp.data.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },
  async uploadProfileImage({ commit, dispatch }, data) {
    try {
      // Use GraphQL mutation through Nexus to avoid CORS issues with S3 service
      const result = await nexusClient.mutate({
        mutation: UPLOAD_PROFILE_IMAGE_MUTATION,
        variables: {
          input: {
            profileImage: data.profile_image
          }
        }
      });

      const response = result.data?.uploadProfileImage;

      if (response?.ok && response?.profile) {
        // Update the store with the new profile image
        commit(MutationTypes.SET_PROFILE_IMAGE, response.profile);

        // Return in the format expected by AvatarUploader.vue
        return {
          body: JSON.stringify({
            data: {
              small: response.profile.small,
              medium: response.profile.medium,
              large: response.profile.large
            }
          })
        };
      }

      console.error('Profile image upload failed:', response?.message);
      return null;
    } catch (err: any) {
      // Log structured error information for debugging
      console.error('Profile image upload failed:', {
        message: err.message,
        graphQLErrors: err.graphQLErrors
      });
      return null;
    }
  },
  async uploadImage({ commit, dispatch }, data) {
    try {
      const resp = await axios.post(`${import.meta.env.VITE_S3_PROFILE_IMAGE_SERVICE}/image`, data);
      return resp.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },
  async removeProfileImage({ commit, dispatch }, data) {
    try {
      // Use GraphQL mutation through Nexus to avoid CORS issues
      const result = await nexusClient.mutate({
        mutation: REMOVE_PROFILE_IMAGE_MUTATION
      });

      const response = result.data?.removeProfileImage;
      if (response?.ok) {
        // Clear the profile image in the store
        commit(MutationTypes.SET_PROFILE_IMAGE, null);
        return { success: true };
      }

      console.error('Profile image removal failed:', response?.message);
      return null;
    } catch (err: any) {
      console.error('Profile image removal failed:', err.message);
      return err;
    }
  },
  async getTokens({ commit, dispatch }, data) {
    try {
      let API_ENDPOINT = `${import.meta.env.VITE_REDIS}/get-tokens`;
      if (data?.public_id) {
        API_ENDPOINT = `${API_ENDPOINT}?public_id=${data?.public_id}`;
      }
      const resp = await axios.get(`${API_ENDPOINT}`);
      return resp.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },
  // eslint-disable-next-line consistent-return
  async getSponsorLinkByShareCode({ commit, dispatch }, payload) {
    const shareCode = payload?.share_code;
    if (!shareCode) {
      console.warn('[getSponsorLinkByShareCode] No share_code provided');
      return null;
    }

    // Primary source: Nexus GraphQL (reads from MongoDB - single source of truth)
    try {
      console.log('[getSponsorLinkByShareCode] Trying Nexus GraphQL for:', shareCode);
      const { data } = await nexusClient.query({
        query: SPONSOR_LINK_BY_SHARE_CODE_QUERY,
        variables: { shareCode },
        fetchPolicy: 'network-only'
      });

      if (data?.sponsorLinkByShareCode) {
        console.log(
          '[getSponsorLinkByShareCode] Found via Nexus GraphQL:',
          data.sponsorLinkByShareCode
        );
        // Debug: Log programSettings specifically
        console.log('[getSponsorLinkByShareCode] programSettings from GraphQL:', {
          enableInitiativeDesign:
            data.sponsorLinkByShareCode.programSettings?.enableInitiativeDesign,
          programSequenceLength:
            data.sponsorLinkByShareCode.programSettings?.programSequence?.length,
          programSettingsKeys: Object.keys(data.sponsorLinkByShareCode.programSettings || {})
        });
        // Transform to match the expected format used by useSponsorManagement.ts (camelCase)
        const sponsorLink = data.sponsorLinkByShareCode;
        return {
          _id: sponsorLink.id,
          id: sponsorLink.id,
          shareCode: sponsorLink.shareCode,
          createdBy: sponsorLink.createdBy,
          userId: sponsorLink.userId,
          type: sponsorLink.type,
          programSettings: sponsorLink.programSettings || {}
        };
      }
    } catch (nexusErr) {
      console.warn(
        '[getSponsorLinkByShareCode] Nexus GraphQL failed, falling back to Redis:',
        nexusErr
      );
    }

    // Fallback: Redis service (legacy)
    try {
      console.log('[getSponsorLinkByShareCode] Trying Redis service for:', shareCode);
      const resp = await axios.get(
        `${import.meta.env.VITE_REDIS_SERVICE}/v1/sponsor-link/share-code?share_code=${shareCode}`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      console.log('[getSponsorLinkByShareCode] Found via Redis:', resp.data.data);
      return resp.data.data;
    } catch (redisErr) {
      console.warn('[getSponsorLinkByShareCode] Redis service also failed:', redisErr);
      return null;
    }
  },

  /**
   * Get organization basic info by ID using Nexus GraphQL
   * This bypasses the sponsor service's invite link lookup which has URL mismatch issues
   *
   * @param organizationId - The organization ID to look up
   * @returns Organization basic info (id, name, logo, domains) or null if not found
   */
  // eslint-disable-next-line consistent-return
  async getOrganizationById({ commit, dispatch }, organizationId: string) {
    if (!organizationId) {
      console.warn('[getOrganizationById] No organizationId provided');
      return null;
    }

    try {
      console.log('[getOrganizationById] Fetching organization via Nexus GraphQL:', organizationId);
      const { data } = await nexusClient.query({
        query: ORGANIZATION_BASIC_INFO_QUERY,
        variables: { organizationId },
        fetchPolicy: 'network-only'
      });

      if (data?.organizationBasicInfo) {
        console.log('[getOrganizationById] Found organization:', data.organizationBasicInfo);
        return data.organizationBasicInfo;
      }

      console.warn('[getOrganizationById] Organization not found for ID:', organizationId);
      return null;
    } catch (err) {
      console.error('[getOrganizationById] Failed to fetch organization:', err);
      return null;
    }
  },

  // eslint-disable-next-line consistent-return
  async getWalletByOwnerId({ commit, dispatch }, payload) {
    try {
      const resp = await axios.get(
        `${import.meta.env.VITE_REDIS_SERVICE}/wallet?owner_id=${payload?.owner_id}`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return resp.data.data;
    } catch (err) {
      console.log(err);
    }
  },
  async getProductInfos({ commit, dispatch }) {
    try {
      // Use fetch API to force Content-Type header (axios strips it from GET requests)
      // The backend's Middy httpJsonBodyParser requires Content-Type: application/json
      const response = await fetch(`${import.meta.env.VITE_PAYMENT_SERVICE}/product-information`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('apollo-token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const resp = await response.json();
      // Validate API response with Zod - prevents "c.map is not a function" errors
      return safeValidate(
        ProductInfoArraySchema,
        resp.data,
        [], // fallback to empty array if validation fails
        'getProductInfos'
      );
    } catch (err) {
      console.log(err);
      return []; // return empty array on error, not the error object
    }
  },
  async getAllStudents({ commit, dispatch }) {
    try {
      const resp = await axios.get(`${import.meta.env.VITE_STUDENT_SERVICE}/students`);
      return resp.data.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },
  async disconnectUserFromCalendar({ commit, dispatch }, payload) {
    try {
      const resp = await axios.post(
        `${import.meta.env.VITE_CALENDAR_SERVICE}/calendar/disconnect`,
        payload
      );
      return resp.data.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },
  async updateInvitedUserFlag({ commit, dispatch }, data) {
    try {
      const resp = await axios.patch(`${import.meta.env.VITE_USER_SERVICE}/user/invitedUser`, data);
      return resp.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },
  // eslint-disable-next-line consistent-return
  async getStackholders({ commit, dispatch }, data) {
    try {
      const resp = await axios.get(
        `${import.meta.env.VITE_USER_SERVICE}/stackholders?user_type=${data.user_type}&program_id=${
          data.program_id
        }&document_type=${data.document_type}`
      );
      return resp.data.data;
    } catch (err) {
      console.log(err);
    }
  },
  async addStackholders({ commit, dispatch }, data) {
    try {
      const resp = await axios.post(`${import.meta.env.VITE_USER_SERVICE}/add/organizer`, data, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return resp;
    } catch (error) {
      console.log(error.response);
      return error.response.data;
    }
  },
  async removeStackholder({ commit, dispatch }, data) {
    try {
      const resp = await axios.delete(
        `${import.meta.env.VITE_USER_SERVICE}/remove/organizer?user_id=${data.user_id}&program_id=${
          data.program_id
        }&document_type=${data.document_type}`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return resp;
    } catch (error) {
      console.log(error.response);
      return error.response.data;
    }
  },
  // eslint-disable-next-line consistent-return
  async getLatestDocumentByUserId({ commit, dispatch }, data) {
    // Guard: if caller passed a falsy or blank user_id, skip the request to prevent 400s
    if (!data?.user_id || data.user_id.trim() === '') {
      return null;
    }

    try {
      const resp = await axios.get(
        `${import.meta.env.VITE_PROGRAM_DOC}/latest/adk-index?document_type=${
          data.document_type
        }&user_id=${data.user_id}`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return resp.data.data;
    } catch (err) {
      console.log(err);
      // return err;
    }
  },

  // eslint-disable-next-line consistent-return
  async makeOffer({ commit, dispatch }, data) {
    try {
      const resp = await axios.post(
        `${import.meta.env.VITE_STUDENT_SERVICE}/resolve-student-application`,
        data
      );
      return resp.data.data;
    } catch (err) {
      console.log((err as any)?.response);
    }
  },
  async getSponsorLinks({ commit, dispatch }) {
    try {
      const resp = await axios.get(`${import.meta.env.VITE_REDIS}/get-sponsor-links`);
      return resp.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },
  async getTokensBySponsorIdAnddata({ commit, dispatch }, data) {
    try {
      const resp = await axios.get(
        `${import.meta.env.VITE_SPONSOR_SERVICE_LINKS}/tokens?owner_id=${data.owner_id}`
      );
      return resp.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },

  async transferTokens({ commit, dispatch }, data) {
    try {
      const resp = await axios.post(`${import.meta.env.VITE_REDIS}/transfer-tokens`, data);
      return resp.data;
    } catch (err) {
      return err.response.data;
    }
  },

  async saveDeliverableSetting({ commit, dispatch }, data) {
    try {
      const ENDPOINT_API = import.meta.env.VITE_DELIVERABLE_SETTING;
      const resp = await axios.patch(
        `${ENDPOINT_API}?program_id=${data.program_id}`,
        data.deliverableObj
      );
      commit(MutationTypes.GET_PROGRAM, resp.data.data);
      return resp.data.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },

  async getTransactionHistory({ commit, dispatch }) {
    try {
      const resp = await axios.get(`${import.meta.env.VITE_REDIS}/get-transactions`);
      return resp.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },

  async createSponsorLinks({ commit, dispatch }, data) {
    try {
      const resp = await axios.post(`${import.meta.env.VITE_REDIS}/sponsorlink`, data, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return resp.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },
  async revokeSponsorLink({ commit, dispatch }, data) {
    try {
      const resp = await axios.post(`${import.meta.env.VITE_REDIS}/revokeLink`, data, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return resp.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },
  async createCheckoutSession({ commit, dispatch }, data) {
    try {
      const resp = await axios.post(`${import.meta.env.VITE_STRIPE_CHECKOUT}`, data, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return resp.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },
  async createInvoice({ commit, dispatch }, data) {
    try {
      const resp = await axios.post(`${import.meta.env.VITE_STRIPE_INVOICE}`, data, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return resp.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },
  async updateForceChangedPwdFlag({ commit, dispatch }, data) {
    try {
      const resp = await axios.patch(
        `${import.meta.env.VITE_USER_SERVICE}/user/force_changes_pwd_flag?user_id=${data.user_id}`,
        { force_changed_pwd: data.force_changed_pwd },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return resp.data.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },
  // eslint-disable-next-line consistent-return
  async getProgramByNameOrIdOrSharableUrl({ commit, dispatch }, data) {
    try {
      // The signed-out landing's shell already asked for this document at HTML time
      // (utils/programPrefetch); take that answer instead of a second request.
      const early = prefetchedProgram(data);
      const earlyDoc = early ? await early.catch(() => null) : null;
      if (earlyDoc) {
        // Each caller gets its own copy, exactly as when every call made its own request.
        const doc = JSON.parse(JSON.stringify(earlyDoc));
        commit(MutationTypes.GET_PROGRAM, doc);
        return doc;
      }
      const url = `${import.meta.env.VITE_PROGRAM_DOC}/program?type=${data.type}&program_id=${
        data.program_id
      }&program_name=${data.program_name}&sharable_url=${data.sharable_url}`;
      const resp = await shareInFlight(url, () =>
        axios.get(url, {
          headers: {
            'Content-Type': 'application/json'
          }
        })
      );
      // A shared in-flight response is copied per caller (see above).
      const doc = resp?.data?.data ? JSON.parse(JSON.stringify(resp.data.data)) : resp?.data?.data;
      commit(MutationTypes.GET_PROGRAM, doc);
      return doc;
    } catch (err) {
      console.log(err);
    }
  },
  async createStudent({ commit, dispatch }, data) {
    try {
      const resp = await axios.post(`${import.meta.env.VITE_STUDENT_SERVICE}/student`, data);
      commit(MutationTypes.GET_PROGRAM, resp.data.data);
      return resp.data.data;
    } catch (err: any) {
      // Handle network errors (no response from server)
      if (!err.response) {
        console.error('[createStudent] Network error:', {
          program_id: data?.program_id,
          error: err.message
        });
        return {
          error: true,
          status: 0,
          message: 'Network request failed. Please check your connection and try again.'
        };
      }

      // Handle HTTP errors (server responded with error)
      console.error('[createStudent] HTTP error:', {
        status: err.response.status,
        message: err.response.data?.message,
        program_id: data?.program_id
      });

      return {
        error: true,
        status: err.response.status,
        message: err.response.data?.message || 'Failed to create student record',
        data: err.response.data
      };
    }
  },
  async createAnonymousStudent({ commit, dispatch }, data) {
    try {
      const resp = await axios.post(
        `${import.meta.env.VITE_STUDENT_SERVICE}/student/anonymous`,
        data,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      // commit(MutationTypes.GET_PROGRAM, resp.data.data);
      return resp.data.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },

  /**
   * Update student document preferences (e.g., hideCelebrationDialog)
   * Uses Nexus GraphQL API for cross-device persistence.
   *
   * FALLBACK BEHAVIOR: If this fails (e.g., Nexus backend not deployed yet),
   * localStorage in the calling component provides immediate fallback.
   * The preference will sync to database once the backend is available.
   *
   * @param data.student_id - The student document ID
   * @param data.preferences - Object containing preference fields to update
   */
  async updateStudentPreference({ commit, dispatch }, data) {
    try {
      const { UPDATE_STUDENT_PREFERENCE_MUTATION } = await import(
        '@/services/graphql/nexus/student'
      );
      const { default: nexusClient } = await import('@/apollo/nexusClient');

      const { data: result } = await nexusClient.mutate({
        mutation: UPDATE_STUDENT_PREFERENCE_MUTATION,
        variables: {
          studentId: data.student_id,
          data: data.preferences
        }
      });

      // Note: We don't update local state here because:
      // 1. The preference is persisted to the database
      // 2. It will be read from the database on next page load
      // 3. localStorage already provides immediate local feedback
      return { success: true, data: result?.updateStudentPreference };
    } catch (err) {
      // Log as warn instead of error since localStorage fallback handles this gracefully
      // This prevents alarming console errors when Nexus backend isn't deployed yet
      console.warn(
        '[updateStudentPreference] Database sync failed (localStorage fallback active):',
        err?.message || err
      );
      return {
        error: true,
        message: err?.message || 'Failed to update preference'
      };
    }
  },

  async getDataWithShareableUrl({ commit, dispatch }, data) {
    const API_ENDPOINT = import.meta.env.VITE_TEAM_SERVICE;
    try {
      const resp = await axios.get(
        `${API_ENDPOINT}/sharable-url?sharable_url=${data.sharable_url}&document_type=${data.document_type}`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      commit(MutationTypes.GET_PROGRAM, resp.data.data);
      return resp.data.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },

  async getAllPrograms({ commit, dispatch }, data) {
    const API_ENDPOINT = import.meta.env.VITE_PROGRAM_LIST;
    try {
      const resp = await axios.get(`${API_ENDPOINT}?skip=${0}&limit=${0}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return resp.data.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },

  async createScope({ commit, dispatch }, userId) {
    const API_ENDPOINT = import.meta.env.VITE_PROGRAM_DOC;
    try {
      const data = {
        user_id: userId
      };
      const resp = await axios.post(`${API_ENDPOINT}/scope`, data, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return resp.data.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },

  async updateScope({ commit, dispatch }, data) {
    const API_ENDPOINT = import.meta.env.VITE_PROGRAM_DOC;
    try {
      const resp = await axios.patch(`${API_ENDPOINT}/scope`, data, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return resp.data.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },

  async fetchScopeState({ commit, dispatch }, data) {
    const API_ENDPOINT = import.meta.env.VITE_PROGRAM_DOC;
    try {
      const resp = await axios.get(`${API_ENDPOINT}/scope?user_id=${data}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return resp.data.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },

  // eslint-disable-next-line consistent-return
  async getVisitedStudents({ commit, dispatch }, data) {
    try {
      const resp = await axios.get(`${import.meta.env.VITE_STUDENT_SERVICE}/visited`);
      return resp.data.data;
    } catch (err) {
      console.log(err);
    }
  },

  async getOrganizationByLink({ commit, dispatch }, link) {
    try {
      const resp = await axios.get(
        `${import.meta.env.VITE_SPONSOR_SERVICE}/get-org-link?invite_link=${link}`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return resp.data.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },

  async getPathways() {
    try {
      const { LANDING_PUBLISHED_PROGRAMS_QUERY } = await import(
        '@/services/graphql/nexus/programs'
      );
      const { default: nexusClient } = await import('@/apollo/nexusClient');
      const { data } = await nexusClient.query({ query: LANDING_PUBLISHED_PROGRAMS_QUERY });
      const pathways = data?.programs?.aggregations?.pathways ?? [];
      // Validate API response with Zod - prevents "pathways.filter is not a function" errors
      return safeValidate(
        PathwaysArraySchema,
        pathways,
        [], // fallback to empty array if validation fails
        'getPathways'
      );
    } catch (err) {
      console.error('[getPathways] GraphQL error', err);
      return [];
    }
  },

  async saveMetaSettings({ commit, dispatch }, payload) {
    try {
      const resp = await axios.post(`${import.meta.env.VITE_PROGRAM_DOC}/meta/settings`, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      commit(MutationTypes.GET_PROGRAM, resp.data.data);
      return resp.data.data;
    } catch (err) {
      console.log(err.response);
      return err.response;
    }
  },

  async fetchTemplates() {
    try {
      const { LANDING_PUBLISHED_PROGRAMS_QUERY } = await import(
        '@/services/graphql/nexus/programs'
      );
      const { default: nexusClient } = await import('@/apollo/nexusClient');
      const { data } = await nexusClient.query({ query: LANDING_PUBLISHED_PROGRAMS_QUERY });
      return data?.programs?.aggregations?.templates ?? [];
    } catch (err) {
      console.error('[fetchTemplates] GraphQL error', err);
      return [];
    }
  },
  async updateCompleted({ commit, dispatch }, data) {
    const API_ENDPOINT = import.meta.env.VITE_PROGRAM_DOC;
    try {
      const resp = await axios.post(
        `${import.meta.env.VITE_PROGRAM_DOC}/update-completed`,
        {
          adkIndex: data.adkIndex,
          collection_name: data.collection_name,
          name: data.name,
          program_id: data.program_id,
          isCompleted: true
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      console.log(resp, 'respt');
      return resp.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },
  async fetchTemplateById({ commit, dispatch }, templateId) {
    const API_ENDPOINT = import.meta.env.VITE_PROGRAM_SCHEMA_SERVICE;
    const token = getAccessToken();
    console.log('🔵 [fetchTemplateById] START - templateId:', templateId);
    console.log('🔵 [fetchTemplateById] API_ENDPOINT:', API_ENDPOINT);
    console.log('🔵 [fetchTemplateById] token exists:', !!token);
    try {
      const resp = await axios.get(`${API_ENDPOINT}/schema?program_schema_id=${templateId}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });
      console.log('✅ [fetchTemplateById] SUCCESS:', resp.data);
      commit(MutationTypes.GET_PROGRAM, resp.data);
      return resp.data;
    } catch (err) {
      console.error('❌ [fetchTemplateById] ERROR:', err);
      console.error('❌ [fetchTemplateById] Error response:', err.response?.data);
      return err;
    }
  },

  async cloneTemplate({ commit, dispatch }, data) {
    const API_ENDPOINT = import.meta.env.VITE_PROGRAM_SCHEMA_SERVICE;
    try {
      const resp = await axios.post(`${API_ENDPOINT}/clone/template`, data, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return resp.data.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },

  async createTemplate({ commit, dispatch }, data) {
    const API_ENDPOINT = import.meta.env.VITE_PROGRAM_SCHEMA_SERVICE;
    const token = getAccessToken();
    console.log('🔵 [createTemplate] START');
    console.log('🔵 [createTemplate] API_ENDPOINT:', API_ENDPOINT);
    console.log('🔵 [createTemplate] token exists:', !!token);
    console.log(
      '🔵 [createTemplate] token preview:',
      token ? `${token.substring(0, 50)}...` : 'null'
    );
    try {
      console.log('🔵 [createTemplate] Making POST request to:', `${API_ENDPOINT}/template`);
      const resp = await axios.post(`${API_ENDPOINT}/template`, null, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });
      console.log('✅ [createTemplate] SUCCESS - Response status:', resp.status);
      console.log('✅ [createTemplate] Response data:', JSON.stringify(resp.data, null, 2));
      console.log('✅ [createTemplate] Returning resp.data.data:', resp.data.data);
      return resp.data.data;
    } catch (err) {
      console.error('❌ [createTemplate] ERROR:', err);
      console.error('❌ [createTemplate] Error response:', err.response?.data);
      console.error('❌ [createTemplate] Error status:', err.response?.status);
      return null;
    }
  },

  async cloneProgram({ commit, dispatch }, data) {
    // const API_ENDPOINT = 'http://localhost:3000/stage';
    const API_ENDPOINT = import.meta.env.VITE_PROGRAM_SCHEMA_SERVICE;
    try {
      const resp = await axios.post(`${API_ENDPOINT}/clone/program`, data, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return resp.data.data;
    } catch (err) {
      console.log(err.response.data);
      return err.response.data;
    }
  },

  async cloneProgramData({ commit, dispatch }, data) {
    const API_ENDPOINT = import.meta.env.VITE_CLONE_PROGRAM;
    try {
      const resp = await axios.post(`${API_ENDPOINT}/clone-program`, data, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      commit(MutationTypes.GET_PROGRAM, resp.data.data);
      return resp.data;
    } catch (err) {
      console.log(err.response.data);
      return err.response.data;
    }
  },

  async updateProgramTemplate({ commit, dispatch }, data) {
    const API_ENDPOINT = import.meta.env.VITE_PROGRAM_SCHEMA_SERVICE;
    const token = getAccessToken();
    console.log('🔵 [updateProgramTemplate] START - program_schema_id:', data.program_schema_id);
    console.log('🔵 [updateProgramTemplate] API_ENDPOINT:', API_ENDPOINT);
    console.log('🔵 [updateProgramTemplate] token exists:', !!token);
    console.log('🔵 [updateProgramTemplate] payload:', data.payload);
    try {
      const resp = await axios.patch(
        `${API_ENDPOINT}/schema?program_schema_id=${data.program_schema_id}`,
        data.payload,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      );
      console.log('✅ [updateProgramTemplate] SUCCESS:', resp.data);
      return resp.data;
    } catch (err) {
      console.error('❌ [updateProgramTemplate] ERROR:', err);
      console.error('❌ [updateProgramTemplate] Error response:', err.response?.data);
      return err.response.data;
    }
  },

  async fetchActivities({ commit, dispatch }) {
    const API_ENDPOINT = import.meta.env.VITE_PROGRAM_SCHEMA_SERVICE;
    try {
      const resp = await axios.get(`${API_ENDPOINT}/activities`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return resp.data.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },

  async getSharableUrlByName({ commit, dispatch }, data) {
    let nameValue = data?.name?.toString().toLowerCase();

    // replace & with and
    nameValue = nameValue.split(/&+/).join('-and-');

    // remove invalid characters
    nameValue = nameValue.split(/[^a-z0-9]/).join('-');

    // remove duplicates
    nameValue = nameValue.split(/-+/).join('-');

    // trim leading & trailing characters
    nameValue = nameValue.trim('-');

    let origin;
    if (import.meta.env.VITE_NODE_ENV === 'stage') {
      origin = 'stage.pilotcity.com';
    } else if (import.meta.env.VITE_NODE_ENV === 'production') {
      origin = 'pilotcity.com';
    } else {
      origin = 'develope.pilotcity.com';
    }
    const sharable_url = `${origin}/${data.type}/${nameValue}`;
    return sharable_url;
  },

  async saveCheckoutQualification({ commit, dispatch }, data) {
    try {
      const resp = await axios.post(
        `${import.meta.env.VITE_PROGRAM_DOC}/checkout/qualification`,
        data,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      commit(MutationTypes.GET_PROGRAM, resp.data.data);
      return resp.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },

  async saveActivitySettings({ commit, dispatch }, data) {
    try {
      const resp = await axios.patch(
        `${import.meta.env.VITE_PROGRAM_DOC}/activity/settings?document_type=${
          data?.document_type
        }&document_id=${data?.document_id}&adk_name=${data?.adk_name}`,
        data.payload,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      commit(MutationTypes.GET_PROGRAM, resp.data.data);
      return resp.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },

  // eslint-disable-next-line consistent-return
  async getUserPermissions({ commit, dispatch }) {
    try {
      const token = getAccessToken();
      if (!token) {
        console.error('No authentication token found');
        return null;
      }

      const response = await axios.get(`${import.meta.env.VITE_AUTH0}/permissions`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching user permissions:', error);

      // Handle specific error cases
      if (error.response) {
        // Server responded with an error status
        if (error.response.status === 401 || error.response.status === 403) {
          console.error('Authentication failed for permissions endpoint');
          // Could trigger a token refresh here if needed
        } else if (error.response.status >= 500) {
          console.error('Server error when fetching permissions');
        }
      } else if (error.request) {
        // Network error (including CORS)
        console.error('Network error when fetching permissions:', error.message);
      }

      // Return null instead of undefined so calling code can handle gracefully
      return null;
    }
  },

  // eslint-disable-next-line consistent-return
  async getPermissionsByRole({ commit, dispatch }, data) {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_USER_SERVICE}/permissions?table=${data?.table}&roles=${
          data?.roles
        }`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data.data;
    } catch (error) {
      console.log(error);
    }
  },

  async fetchAllOrganizations({ commit, dispatch }) {
    try {
      const res = await axios.get(`${import.meta.env.VITE_USER_SERVICE}/organization`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return res.data.data;
    } catch (error) {
      return error;
    }
  },

  async checkoutFreeProgram({ commit, dispatch }, payload) {
    const API_ENDPOINT = import.meta.env.VITE_REDIS_SERVICE;
    try {
      const resp = await axios.post(`${API_ENDPOINT}/checkout-free`, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      commit(MutationTypes.GET_PROGRAM, resp.data.data);
      return resp.data.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },

  getDomainByEnvironmentName({ commit, dispatch }) {
    let domain;
    if (import.meta.env.VITE_NODE_ENV === 'development') {
      domain = 'develop.pilotcity.com';
    } else if (import.meta.env.VITE_NODE_ENV === 'stage') {
      domain = 'stage.pilotcity.com';
    } else {
      domain = 'pilotcity.com';
    }
    return domain;
  },

  async saveDraft({ commit, dispatch }, payload) {
    try {
      const resp = await axios.post(`${import.meta.env.VITE_REVISION_SERVICE}/draft`, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return resp.data.data;
    } catch (err) {
      console.log(err.response);
      return err.response;
    }
  },

  // eslint-disable-next-line consistent-return
  async deleteDraft({ commit, dispatch }, payload) {
    try {
      const resp = await axios.delete(
        `${import.meta.env.VITE_REVISION_SERVICE}/draft?program_id=${payload?.program_id}`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return resp.data.data;
    } catch (err) {
      console.log(err.response);
    }
  },

  // eslint-disable-next-line consistent-return
  async fetchDraft({ commit, dispatch }, payload) {
    try {
      const resp = await axios.get(
        `${import.meta.env.VITE_REVISION_SERVICE}/draft?program_id=${payload?.program_id}`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      commit(MutationTypes.GET_PROGRAM, resp.data.data);
      return resp.data.data;
    } catch (err) {
      console.log(err.response);
    }
  },

  async reAttachVideoaskResponse({ commit, dispatch }, data) {
    try {
      const resp = await axios.post(
        `${import.meta.env.VITE_VIDEOASK_SERVICE}/reattach/videoask`,
        data,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('apollo-token')}`
          }
        }
      );
      return resp.data;
    } catch (err) {
      console.log(err);
      // Harden: a network/timeout error has no `.response`, so `err.response.data`
      // would itself throw — leaving the button stuck in its loading state. Always
      // return a well-formed error object the caller can surface.
      return (
        err?.response?.data || {
          error: true,
          message: 'Couldn’t reach the response service. Please try again in a moment.'
        }
      );
    }
  },
  // eslint-disable-next-line consistent-return
  async saveActivityStartedOrEndedTime({ commit, dispatch }, data) {
    try {
      const resp = await axios.post(`${import.meta.env.VITE_STUDENT_SERVICE}/activity/time`, data, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return resp.data.data;
    } catch (err) {
      console.log(err);
    }
  },
  // eslint-disable-next-line consistent-return
  async saveRewardsAndCredentials({ commit, dispatch }, data) {
    try {
      const resp = await axios.post(
        `${import.meta.env.VITE_PROGRAM_DOC}/rewards-credentials`,
        data,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      commit(MutationTypes.GET_PROGRAM, resp.data.data);
      return resp.data;
    } catch (err) {
      console.log(err);
      // return err;
    }
  },
  // eslint-disable-next-line consistent-return
  async addBadges({ commit, dispatch }, data) {
    // The merit write's home is the Nexus `awardActivityMerits` mutation: atomic idempotency,
    // the caller must own the enrollment, and the legacy crash shapes are handled. It returns
    // the { message, data } shape the callers gate their snackbar on. The legacy
    // student-service /badges Lambda path was removed (D2) once this routing was proven on
    // prod; nothing is committed to GET_PROGRAM here (the legacy commit stored the Lambda's
    // response body as the program doc).
    try {
      const result = await nexusClient.mutate({
        mutation: AWARD_ACTIVITY_MERITS_MUTATION,
        variables: {
          studentId: String(data?.student_id ?? ''),
          adkName: String(data?.adk_name ?? '')
        }
      });
      return (result as any)?.data?.awardActivityMerits ?? null;
    } catch (err) {
      console.warn('[addBadges] awardActivityMerits failed:', (err as any)?.message || err);
      return null;
    }
  },
  // eslint-disable-next-line consistent-return
  async fetchOrgRoles({ commit, dispatch }, data) {
    try {
      const resp = await axios.get(`${import.meta.env.VITE_SPONSOR_SERVICE}/org-roles`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return resp.data.data;
    } catch (err) {
      console.log(err);
    }
  },
  // eslint-disable-next-line consistent-return
  async addMatterPortData({ commit, dispatch }, data) {
    const API_ENDPOINT = import.meta.env.VITE_PROGRAM_DOC;
    try {
      const resp = await axios.post(`${API_ENDPOINT}/matterport`, data, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      commit(MutationTypes.GET_PROGRAM, resp.data.data);
      return resp.data.data;
    } catch (err) {
      console.log(err);
    }
  },
  // eslint-disable-next-line consistent-return
  async saveActivityFeedback({ commit, dispatch }, data) {
    try {
      const resp = await axios.post(
        `${import.meta.env.VITE_STUDENT_SERVICE}/activity-feedback`,
        data,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      commit(MutationTypes.GET_PROGRAM, resp.data.data);
      return resp.data;
    } catch (err) {
      console.log(err);
      // return err;
    }
  },
  // eslint-disable-next-line consistent-return
  async createAutomationProgram({ commit, dispatch }, data) {
    try {
      const { CREATE_PROGRAM_BY_TEMPLATE_ID_MUTATION } = await import(
        '@/services/graphql/nexus/programAutomation'
      );
      const result = await nexusClient.mutate({
        mutation: CREATE_PROGRAM_BY_TEMPLATE_ID_MUTATION,
        variables: {
          templateId: data.program_template_id,
          data
        }
      });
      return result.data?.createProgramByTemplateId;
    } catch (err) {
      console.error('createAutomationProgram error:', err);
      throw err;
    }
  },
  async simulateScope({ commit, dispatch }, data) {
    try {
      const { SIMULATE_SCOPE_QUERY } = await import('@/services/graphql/nexus/programAutomation');
      const result = await nexusClient.query({
        query: SIMULATE_SCOPE_QUERY,
        variables: { website: data.website || data.websiteUrl || data.companySite },
        fetchPolicy: 'no-cache'
      });
      return result.data?.simulateScope;
    } catch (err) {
      console.error('simulateScope error:', err);
      throw err;
    }
  },
  // eslint-disable-next-line consistent-return
  async simulateProgramDetails({ commit, dispatch }, data) {
    try {
      const { SIMULATE_PROGRAM_DETAILS_QUERY } = await import(
        '@/services/graphql/nexus/programAutomation'
      );
      const result = await nexusClient.query({
        query: SIMULATE_PROGRAM_DETAILS_QUERY,
        variables: {
          website: data.website || data.websiteUrl || data.companySite,
          scope: data.scope || data.programScope
        },
        fetchPolicy: 'no-cache'
      });
      return result.data?.simulateProgramDetails;
    } catch (err) {
      console.error('simulateProgramDetails error:', err);
      throw err;
    }
  },
  async updateAutomationAdk({ commit, dispatch }, data) {
    try {
      const { UPDATE_AUTOMATION_ADK_MUTATION } = await import(
        '@/services/graphql/nexus/programAutomation'
      );
      const programId = data.id || data.programId;

      // When called with an adks array (from SetupWizard), loop over each ADK
      if (Array.isArray(data.adks)) {
        let lastResult = null;
        for (let i = 0; i < data.adks.length; i += 1) {
          const adk = data.adks[i];
          const adkId = adk.id || adk.name || adk.adk_name;
          if (adkId) {
            // eslint-disable-next-line no-await-in-loop
            const result = await nexusClient.mutate({
              mutation: UPDATE_AUTOMATION_ADK_MUTATION,
              variables: { programId, adkId, content: adk }
            });
            lastResult = result.data?.updateAutomationAdk;
          }
        }
        return lastResult;
      }

      // Single ADK update (direct call with adkId)
      // Strip management keys from content — these are GraphQL args, not ADK fields.
      // Passing them through causes Prisma to reject unknown fields like 'programId'.
      const { id: _id, programId: _pid, adkId: _aid, adk_name: _aname, ...contentOnly } = data;
      const result = await nexusClient.mutate({
        mutation: UPDATE_AUTOMATION_ADK_MUTATION,
        variables: {
          programId,
          adkId: data.adk_name || data.adkId,
          content: contentOnly
        }
      });
      return result.data?.updateAutomationAdk;
    } catch (err) {
      console.error('updateAutomationAdk error:', err);
      throw err;
    }
  },

  // Add a new ADK to an existing program through Nexus GraphQL (replaces the legacy
  // add-program-adk REST microservice for the AI Creator edit flow).
  async addAutomationAdk({ commit, dispatch }, data) {
    try {
      const { CREATE_AUTOMATION_ADK_MUTATION } = await import(
        '@/services/graphql/nexus/programAutomation'
      );
      const programId = data.id || data.programId || data.programOrTemplateId;
      const adk = data.adk || data.data;
      const result = await nexusClient.mutate({
        mutation: CREATE_AUTOMATION_ADK_MUTATION,
        variables: { programId, adk }
      });
      return result.data?.createAutomationAdk;
    } catch (err) {
      console.error('addAutomationAdk error:', err);
      throw err;
    }
  },

  async getProblemScope({ commit, dispatch }, payload) {
    try {
      const resp = await axios.post(`${import.meta.env.VITE_STUDENT_SERVICE}/problem`, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return resp.data.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },

  async saveProgressOfSolveADK({ commit, dispatch }, payload) {
    try {
      const resp = await axios.post(`${import.meta.env.VITE_STUDENT_SERVICE}/progress`, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return resp.data.data;
    } catch (err) {
      return err.response;
    }
  },

  async saveBulkScopeProcess({ commit, dispatch }, data) {
    try {
      const { SAVE_BULK_SETUP_SCOPES_MUTATION } = await import(
        '@/services/graphql/nexus/programAutomation'
      );
      const result = await nexusClient.mutate({
        mutation: SAVE_BULK_SETUP_SCOPES_MUTATION,
        variables: {
          bulkId: data.bulkId,
          website: data.website,
          scopes: data.scopes
        }
      });
      return result.data?.saveBulkSetupScopes;
    } catch (err) {
      console.error('saveBulkScopeProcess error:', err);
      throw err;
    }
  },
  async checkValidProgram({ commit, dispatch }, data) {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_CLONE_PROGRAM}/check-program-pay?studentId=${data.studentId}&id=${
          data?.id
        }&amount=${data.amount}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('apollo-token')}`
          }
        }
      );
      return response.data.data;
    } catch (error) {
      console.log(error);
      return error;
    }
  },
  async uploadVideo({ commit, dispatch }, data) {
    const ENDPOINT_API = import.meta.env.VITE_STUDENT_SERVICE;
    try {
      const response = await axios.post(`${ENDPOINT_API}/s3/uploadvideo`, data, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response.data.data;
    } catch (err) {
      console.log(err);
      return err;
    }
  },

  // ============================================================
  // ActivitySchema CRUD Actions
  // ============================================================

  /**
   * Create a new ActivitySchema in the library.
   *
   * @param data - CreateActivitySchemaInput with all required fields
   * @returns ActionResult<ActivitySchema> with success status and created data
   *
   * @example
   * const result = await store.dispatch('user/createActivitySchema', {
   *   name: 'My Activity',
   *   description: 'Activity description',
   *   icon: 'mdi-check',
   *   setupRequired: false,
   *   status: true,
   *   category: 'Do'
   * });
   */
  async createActivitySchema(
    { commit },
    data: CreateActivitySchemaInput
  ): Promise<ActionResult<ActivitySchema>> {
    try {
      const result = await nexusClient.mutate({
        mutation: CREATE_ACTIVITY_SCHEMA_MUTATION,
        variables: { data }
      });

      if (result.data?.createActivitySchemaSimplified) {
        return {
          success: true,
          data: result.data.createActivitySchemaSimplified
        };
      }

      return {
        success: false,
        error: 'Failed to create ActivitySchema: No data returned'
      };
    } catch (error: any) {
      console.error('[createActivitySchema] Error:', error);

      // Extract user-friendly error message
      const errorMessage =
        error?.graphQLErrors?.[0]?.message || error?.message || 'Failed to create ActivitySchema';

      return {
        success: false,
        error: errorMessage
      };
    }
  },

  /**
   * Update an existing ActivitySchema.
   *
   * @param payload - Object containing id and partial update data
   * @param payload.id - The ActivitySchema ID to update
   * @param payload.data - Partial UpdateActivitySchemaInput fields to update
   * @returns ActionResult<ActivitySchema> with success status and updated data
   *
   * @example
   * const result = await store.dispatch('user/updateActivitySchema', {
   *   id: 'activity-schema-id',
   *   data: { name: 'Updated Name', description: 'Updated description' }
   * });
   */
  async updateActivitySchema(
    { commit },
    payload: { id: string; data: UpdateActivitySchemaInput }
  ): Promise<ActionResult<ActivitySchema>> {
    try {
      const result = await nexusClient.mutate({
        mutation: UPDATE_ACTIVITY_SCHEMA_MUTATION,
        variables: {
          id: payload.id,
          data: payload.data
        }
      });

      if (result.data?.updateActivitySchemaSimplified) {
        return {
          success: true,
          data: result.data.updateActivitySchemaSimplified
        };
      }

      return {
        success: false,
        error: 'Failed to update ActivitySchema: No data returned'
      };
    } catch (error: any) {
      console.error('[updateActivitySchema] Error:', error);

      // Handle permission errors with user-friendly message
      const graphQLError = error?.graphQLErrors?.[0];
      if (graphQLError?.extensions?.code === 'FORBIDDEN') {
        return {
          success: false,
          error:
            'You do not have permission to edit this activity. Only the creator or a superadmin can modify it.'
        };
      }

      const errorMessage =
        graphQLError?.message || error?.message || 'Failed to update ActivitySchema';

      return {
        success: false,
        error: errorMessage
      };
    }
  },

  /**
   * Delete (archive) an ActivitySchema.
   *
   * @param id - The ActivitySchema ID to delete
   * @returns ActionResult<boolean> with success status
   *
   * @example
   * const result = await store.dispatch('user/deleteActivitySchema', 'activity-schema-id');
   */
  async deleteActivitySchema({ commit }, id: string): Promise<ActionResult<boolean>> {
    try {
      const result = await nexusClient.mutate({
        mutation: DELETE_ACTIVITY_SCHEMA_MUTATION,
        variables: { id }
      });

      if (result.data?.deleteActivitySchema) {
        return {
          success: true,
          data: true
        };
      }

      return {
        success: false,
        error: 'Failed to delete ActivitySchema'
      };
    } catch (error: any) {
      console.error('[deleteActivitySchema] Error:', error);

      // Handle permission errors with user-friendly message
      const graphQLError = error?.graphQLErrors?.[0];
      if (graphQLError?.extensions?.code === 'FORBIDDEN') {
        return {
          success: false,
          error:
            'You do not have permission to delete this activity. Only the creator or a superadmin can delete it.'
        };
      }

      const errorMessage =
        graphQLError?.message || error?.message || 'Failed to delete ActivitySchema';

      return {
        success: false,
        error: errorMessage
      };
    }
  },

  /**
   * Get ActivitySchema library with optional filters.
   *
   * @param filter - Optional filter parameters
   * @param filter.category - Filter by category (Pre, Do, Reflect, etc.)
   * @param filter.statusType - Filter by status (draft, published, archived)
   * @param filter.search - Search by name (case-insensitive contains)
   * @param filter.skip - Pagination offset (default: 0)
   * @param filter.take - Pagination limit (default: 20)
   * @returns ActionResult<ActivitySchemaLibraryResult> with items, totalCount, hasMore
   *
   * @example
   * const result = await store.dispatch('user/getActivitySchemaLibrary', {
   *   category: 'Do',
   *   search: 'project',
   *   skip: 0,
   *   take: 20
   * });
   */
  async getActivitySchemaLibrary(
    { commit },
    filter?: ActivitySchemaLibraryFilter
  ): Promise<ActionResult<ActivitySchemaLibraryResult>> {
    try {
      const result = await nexusClient.query({
        query: GET_ACTIVITY_SCHEMA_LIBRARY_QUERY,
        variables: {
          category: filter?.category,
          statusType: filter?.statusType,
          search: filter?.search,
          skip: filter?.skip ?? 0,
          take: filter?.take ?? 20
        },
        fetchPolicy: 'network-only' // Always fetch fresh data for library
      });

      if (result.data?.activitySchemaLibrary) {
        return {
          success: true,
          data: result.data.activitySchemaLibrary
        };
      }

      return {
        success: false,
        error: 'Failed to fetch ActivitySchema library: No data returned'
      };
    } catch (error: any) {
      console.error('[getActivitySchemaLibrary] Error:', error);

      const errorMessage =
        error?.graphQLErrors?.[0]?.message ||
        error?.message ||
        'Failed to fetch ActivitySchema library';

      return {
        success: false,
        error: errorMessage
      };
    }
  },

  /**
   * Clone an ActivitySchema for embedding in a Program.
   * Supports the "Both" save option by creating library entry and clone atomically.
   *
   * @param payload - Object containing activitySchemaId and programId
   * @param payload.activitySchemaId - The source ActivitySchema ID to clone
   * @param payload.programId - The target Program ID
   * @returns ActionResult<ActivitySchema> with cloned ActivitySchema data
   *
   * @example
   * const result = await store.dispatch('user/cloneActivitySchemaToProgram', {
   *   activitySchemaId: 'source-activity-id',
   *   programId: 'target-program-id'
   * });
   */
  async cloneActivitySchemaToProgram(
    { commit },
    payload: { activitySchemaId: string; programId: string }
  ): Promise<ActionResult<ActivitySchema>> {
    try {
      const result = await nexusClient.mutate({
        mutation: CLONE_ACTIVITY_SCHEMA_TO_PROGRAM_MUTATION,
        variables: {
          activitySchemaId: payload.activitySchemaId,
          programId: payload.programId
        }
      });

      const cloneResult: CloneActivitySchemaResult = result.data?.cloneActivitySchemaToProgram;

      if (cloneResult?.success && cloneResult?.data) {
        return {
          success: true,
          data: cloneResult.data
        };
      }

      return {
        success: false,
        error: cloneResult?.error || 'Failed to clone ActivitySchema: No data returned'
      };
    } catch (error: any) {
      console.error('[cloneActivitySchemaToProgram] Error:', error);

      const errorMessage =
        error?.graphQLErrors?.[0]?.message || error?.message || 'Failed to clone ActivitySchema';

      return {
        success: false,
        error: errorMessage
      };
    }
  },

  // ============================================================
  // Video Questions Widget - Nexus VideoAsk Operations
  // ============================================================

  /**
   * Reattach a VideoAsk response for the video-questions widget (via Nexus GraphQL)
   *
   * This queries the VideoAsk API through Nexus to find if the user has completed the form,
   * then stores the response in the Student document.
   *
   * @param data - { user_id, program_id, adk_name }
   * @returns { success, message, error? }
   */
  async reAttachVideoQuestionsResponse(
    { commit },
    data: { user_id: string; program_id: string; adk_name: string }
  ): Promise<{ success: boolean; message?: string; error?: boolean }> {
    try {
      const input: ReattachVideoAskInput = {
        userId: data.user_id,
        programId: data.program_id,
        adkName: data.adk_name
      };

      const result = await nexusClient.mutate<{
        reAttachVideoaskResponse: VideoAskOperationResult;
      }>({
        mutation: REATTACH_VIDEOASK_RESPONSE_MUTATION,
        variables: { input }
      });

      const response = result.data?.reAttachVideoaskResponse;

      if (response?.success) {
        return {
          success: true,
          message: response.message || 'Response synced successfully'
        };
      }

      return {
        success: false,
        error: true,
        message: response?.message || 'Could not find a response to sync'
      };
    } catch (error: any) {
      console.error('[reAttachVideoQuestionsResponse] Error:', error);
      return {
        success: false,
        error: true,
        message: error?.message || 'An error occurred while syncing response'
      };
    }
  },

  /**
   * Delete a VideoAsk response for the video-questions widget (via Nexus GraphQL)
   *
   * Removes the VideoAsk response from the Student document, allowing the user
   * to record a new response.
   *
   * @param data - { user_type, program_id, public_id, adk_name, key_name }
   * @returns { success, message, error? }
   */
  async deleteVideoQuestionsResponse(
    { commit },
    data: {
      user_type: string;
      program_id: string;
      public_id?: string;
      // STUDENT_INSTANCES: the loaded instance's _id, supplied only under the flag.
      student_id?: string;
      adk_name: string;
      key_name: string;
    }
  ): Promise<{ success: boolean; message?: string; error?: boolean }> {
    try {
      const input: DeleteVideoAskInput = {
        userType: data.user_type,
        programId: data.program_id,
        publicId: data.public_id,
        adkName: data.adk_name,
        keyName: data.key_name,
        // STUDENT_INSTANCES: forward only when the caller supplies it (flag-gated at
        // the call site) so a frontend-first deploy never sends a GraphQL input field
        // an older backend schema doesn't declare.
        ...(data.student_id ? { studentId: data.student_id } : {})
      };

      const result = await nexusClient.mutate<{
        deleteVideoAskResponse: VideoAskOperationResult;
      }>({
        mutation: DELETE_VIDEOASK_RESPONSE_MUTATION,
        variables: { input }
      });

      const response = result.data?.deleteVideoAskResponse;

      if (response?.success) {
        return {
          success: true,
          message: response.message || 'Response deleted successfully'
        };
      }

      return {
        success: false,
        error: true,
        message: response?.message || 'Failed to delete response'
      };
    } catch (error: any) {
      console.error('[deleteVideoQuestionsResponse] Error:', error);
      return {
        success: false,
        error: true,
        message: error?.message || 'An error occurred while deleting response'
      };
    }
  },

  /**
   * Refresh VideoAsk transcription data from the VideoAsk API
   *
   * When transcriptions are stuck in "processing" state (webhook missed),
   * this manually fetches fresh data from VideoAsk API and updates stored transcriptions.
   *
   * @param data - { studentId, programId, adkName }
   * @returns { success, message }
   */
  async refreshVideoAskTranscription(
    { commit },
    data: { studentId: string; programId: string; adkName: string }
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const result = await nexusClient.mutate<{
        refreshVideoAskTranscription: { success: boolean; message?: string };
      }>({
        mutation: REFRESH_VIDEOASK_TRANSCRIPTION_MUTATION,
        variables: {
          studentId: data.studentId,
          programId: data.programId,
          adkName: data.adkName
        }
      });

      const response = result.data?.refreshVideoAskTranscription;

      if (response?.success) {
        return {
          success: true,
          message: response.message || 'Transcriptions refreshed successfully'
        };
      }

      return {
        success: false,
        message: response?.message || 'Failed to refresh transcriptions'
      };
    } catch (error: any) {
      console.error('[refreshVideoAskTranscription] Error:', error);
      return {
        success: false,
        message: error?.message || 'An error occurred while refreshing transcriptions'
      };
    }
  },

  // ============================================================
  // Endorsement Widget - Nexus Operations
  // ============================================================

  /**
   * Create an endorsement request (via Nexus GraphQL)
   *
   * Creates an endorsement request, generates a secure token, and sends
   * an email to the endorser with a link to the endorsement dashboard.
   *
   * @param data - CreateEndorsementRequestInput
   * @returns { success, token?, requestId?, message? }
   */
  async createEndorsementRequest(
    { commit },
    data: CreateEndorsementRequestInput
  ): Promise<CreateEndorsementRequestResponse> {
    try {
      const result = await nexusClient.mutate<{
        createEndorsementRequest: CreateEndorsementRequestResponse;
      }>({
        mutation: CREATE_ENDORSEMENT_REQUEST,
        variables: { input: data }
      });

      const response = result.data?.createEndorsementRequest;

      if (response?.success) {
        return {
          success: true,
          token: response.token,
          requestId: response.requestId
        };
      }

      return {
        success: false,
        message: response?.message || 'Failed to create endorsement request'
      };
    } catch (error: any) {
      console.error('[createEndorsementRequest] Error:', error);
      return {
        success: false,
        message:
          error?.graphQLErrors?.[0]?.message ||
          error?.message ||
          'An error occurred while creating endorsement request'
      };
    }
  },

  /**
   * Resend endorsement email (via Nexus GraphQL)
   *
   * Resends the endorsement request email to the endorser.
   *
   * @param data - ResendEndorsementEmailInput
   * @returns { success, error? }
   */
  async resendEndorsementEmail(
    { commit },
    data: ResendEndorsementEmailInput
  ): Promise<ResendEndorsementEmailResponse> {
    try {
      const result = await nexusClient.mutate<{
        resendEndorsementEmail: ResendEndorsementEmailResponse;
      }>({
        mutation: RESEND_ENDORSEMENT_EMAIL,
        variables: { input: data }
      });

      const response = result.data?.resendEndorsementEmail;

      if (response?.success) {
        return {
          success: true
        };
      }

      return {
        success: false,
        message: response?.message || 'Failed to resend endorsement email'
      };
    } catch (error: any) {
      console.error('[resendEndorsementEmail] Error:', error);
      return {
        success: false,
        message:
          error?.graphQLErrors?.[0]?.message ||
          error?.message ||
          'An error occurred while resending email'
      };
    }
  }
};
